import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Application, Container, Graphics, Text, Texture, type Ticker } from 'pixi.js';
import { playSfx } from '../effects/sound.js';
import { usePixiScene } from './pixi/usePixiScene.js';
import {
  LIFT_DURATION_MS,
  SHAKE_DURATION_MS,
  dieLandsAt,
  dieSettle,
  easeOutBack,
  lidClosePose,
  lidLeavePose,
  lidLiftPose,
  liftPose,
  rigShakePose,
  shakePose,
  type LidPose,
} from './diceMotion.js';
import { DICE_PIECES, DiceStage, FRAME, type DicePiece } from './dice/diceStage.js';
import { preferredDensity, preloadPieces, type PieceImages } from '../assets/sprites.js';
import { mixColor, originalTheme, sceneColor } from '../theme.js';
import type { SceneProps } from './types.js';
import type { DiceBet } from '../types.js';

export { DICE_PIECES };

// ---- presentation shape from backend ----
interface DicePresentation {
  dice: number[];
  sum: number;
  win: boolean;
}

// ---- animation constants ----
const PRIMARY_FALLBACK = 0x3a82f6;

const CLOSE_DURATION_MS = 400;
const CLOSE_DURATION_REDUCED_MS = 180;
const SHAKE_DURATION_REDUCED_MS = 300;
const TUMBLE_DURATION_MS = 350;
const WIN_PULSE_DURATION_MS = 400;
const NEAR_MISS_FLICKER_MS = 200;
const IDLE_BREATHE_AMPLITUDE = 0.025;
const DICE_SHAKE_SFX_INTERVAL_MS = 120;
/** How often the idle lid catches the light. */
const GLINT_PERIOD_MS = 3400;
const GLINT_PASS_MS = 900;

// The tile's lacquered gold lid over crimson dice with gold pips. Without a theme
// the scene keeps its plain wooden lid and ivory dice.
function dicePalette() {
  const theme = originalTheme('dice');
  if (!theme) {
    return {
      lid: 0x3b2f1e, lidEdge: 0x8b6344, rim: 0x6b4c2c, rimEdge: 0xa07040, sheen: 0.06,
      die: 0xf5f0e8, dieEdge: 0x8b8070, pip: 0x2c1810, pipHit: null as number | null,
    };
  }
  const gold = sceneColor(theme.accent);
  const red = sceneColor(theme.hue);
  return {
    lid: mixColor(gold, 0x8a3c00, 0.32),
    lidEdge: mixColor(gold, 0xffffff, 0.3),
    rim: mixColor(gold, 0x6a2a00, 0.45),
    rimEdge: mixColor(gold, 0xffffff, 0.45),
    sheen: 0.18,
    die: red,
    dieEdge: mixColor(gold, 0xffffff, 0.2),
    pip: sceneColor(theme.glow),
    pipHit: 0xffffff as number | null,
  };
}

// ---- helpers ----
function parseHex(input: string | undefined, fb: number): number {
  if (!input) return fb;
  const h = input.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(h) ? parseInt(h, 16) : fb;
}

/** One query per scene, read every frame: `matchMedia` itself parses a new query each call. */
function reducedMotionQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
}

function easeOutQuart(t: number): number { return 1 - Math.pow(1 - t, 4); }
function easeOutCubic(t: number): number { return 1 - Math.pow(1 - t, 3); }

function readPresentation(presentation: unknown): DicePresentation | null {
  if (
    presentation != null &&
    typeof presentation === 'object' &&
    'dice' in presentation &&
    Array.isArray((presentation as DicePresentation).dice)
  ) {
    return presentation as DicePresentation;
  }
  return null;
}

// Is this a Hi/Lo near-miss? sum is 10 or 11.
function isNearMissHiLo(sum: number, bet: DiceBet | null | undefined): boolean {
  if (!bet || (bet !== 'low' && bet !== 'high')) return false;
  return sum === 10 || sum === 11;
}

/** Pip offset from the face centre, as a fraction of the die's size. */
const PIP_OFFSET = 0.28;

/**
 * Where the vector die's pips sit, in steps of PIP_OFFSET from the centre (-1, 0 or 1 on
 * each axis), for the server's value.
 */
export function pipLayout(value: number): ReadonlyArray<readonly [number, number]> {
  switch (value) {
    case 1: return [[0, 0]];
    case 2: return [[-1, -1], [1, 1]];
    case 3: return [[-1, -1], [0, 0], [1, 1]];
    case 4: return [[-1, -1], [1, -1], [-1, 1], [1, 1]];
    case 5: return [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]];
    case 6: return [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]];
    default: return [[0, -1], [0, 1]];
  }
}

// Draw all pips for a die face value 1-6.
function drawDieFace(g: Graphics, value: number, size: number, pipColor: number): void {
  const off = size * PIP_OFFSET;
  const r = size * 0.085;
  for (const [px, py] of pipLayout(value)) {
    g.circle(px * off, py * off, r).fill({ color: pipColor, alpha: 1 });
  }
}

/** Fresh, uncached textures per scene: the scene's teardown destroys them, the decoded images stay cached. */
function toTextures(imgs: PieceImages<DicePiece>): Record<DicePiece, Texture> {
  const out = {} as Record<DicePiece, Texture>;
  for (const piece of DICE_PIECES) {
    out[piece] = Texture.from({ resource: imgs[piece], autoGenerateMipmaps: true }, true);
  }
  return out;
}

/** The faces a round shows: three values 1..6, or null when the presentation has none. */
export function faceValues(pres: DicePresentation | null): [number, number, number] | null {
  const d = pres?.dice;
  if (!d || d.length < 3 || d.slice(0, 3).some((v) => !Number.isInteger(v) || v < 1 || v > 6)) return null;
  return [d[0]!, d[1]!, d[2]!];
}

// ---- bowl + dice scene state ----
interface AnimState {
  phase: SceneProps['phase'];
  envelope: SceneProps['envelope'];
  onRevealComplete: () => void;
  turbo: boolean;
  selectionBet: DiceBet | null;
}

// animation sub-phases
type AnimPhase =
  | 'idle'
  | 'closing'
  | 'shaking'
  | 'lifting'
  | 'tumbling'
  | 'settling'
  | 'done';

const LID_SHUT: LidPose = { frame: 0, y: 0, alpha: 1 };
const RIG_REST = { x: 0, y: 0, rotation: 0, scale: 1 };

export function DiceScene({
  config: _config,
  envelope,
  phase,
  onRevealComplete,
  turbo = false,
  selection,
}: SceneProps) {
  const { t } = useTranslation('originals');

  const stateRef = useRef<AnimState>({
    phase,
    envelope,
    onRevealComplete,
    turbo,
    selectionBet: selection?.kind === 'dice' ? selection.bet : null,
  });
  stateRef.current = {
    phase,
    envelope,
    onRevealComplete,
    turbo,
    selectionBet: selection?.kind === 'dice' ? selection.bet : null,
  };

  const onReady = useRef((app: Application, host: HTMLDivElement) => {
    const primary = parseHex(
      getComputedStyle(host).getPropertyValue('--primary').trim() || undefined,
      PRIMARY_FALLBACK,
    );
    const palette = dicePalette();
    // The real-3D render of the card's world, once every layer has decoded. Until then —
    // or for good, if a file never arrives — the scene draws its vector lid and dice.
    let tex: Record<DicePiece, Texture> | null = null;
    let stage: DiceStage | null = null;
    let disposed = false;
    const reducedMq = reducedMotionQuery();
    const prefersReducedMotion = () => reducedMq?.matches ?? false;

    const root = new Container();
    app.stage.addChild(root);

    // Bowl container (the ครอบ)
    const bowlContainer = new Container();
    root.addChild(bowlContainer);

    // Dice containers (3 dice)
    const diceCons: Container[] = [new Container(), new Container(), new Container()];
    for (const dc of diceCons) root.addChild(dc);

    // Pre-built bowl graphics
    const bowlDome = new Graphics();
    const bowlRim = new Graphics();
    bowlContainer.addChild(bowlDome, bowlRim);

    // Pre-built dice face graphics (redrawn on reveal)
    const diceFaceGfx: Graphics[] = diceCons.map(() => new Graphics());
    const diceGlowGfx: Graphics[] = diceCons.map(() => new Graphics());
    for (let i = 0; i < 3; i++) {
      diceCons[i].addChild(diceGlowGfx[i], diceFaceGfx[i]);
    }

    // Colour for a winning sum readout.
    const success = parseHex(
      getComputedStyle(host).getPropertyValue('--success').trim() || undefined,
      0x10b981,
    );

    // Big sum readout shown when the dice settle so players read the total instantly.
    const sumText = new Text({
      text: '',
      style: {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 64,
        fill: 0xffffff,
        fontWeight: '800',
        align: 'center',
      },
    });
    sumText.anchor.set(0.5);
    sumText.alpha = 0;

    // ---- animation state ----
    let animPhase: AnimPhase = 'idle';
    let elapsed = 0;
    let startedRoundId: string | null = null;
    let firedRoundId: string | null = null;
    let lastSfxAt = 0;
    let winPulseElapsed = 0;
    let nearMissFlicker = 0;

    // Captured start-state for the closing tween (the lifted/faded lid + visible dice/sum
    // are interpolated from here back to the covering position as the lid drops).
    let closeFromY = 0;
    let closeFromAlpha = 1;
    let closeFromScale = 1;
    let closeDiceFromAlpha = 1;
    let closeSumFromAlpha = 1;
    // The lid's scale when the shake began (idle breathing), and its pose on the shake's
    // last frame: the lift starts from there instead of snapping to the centre.
    let shakeFromScale = 1;
    let shakeEnd = { x: 0, y: 0, scale: 1 };
    // The card art's lid, wherever it was last put; a close starts from it.
    let lidPose: LidPose = LID_SHUT;
    let closeFromLid: LidPose = LID_SHUT;
    let landed = 0;
    let sumScale = 1;

    // Settled die values (shown after reveal)
    let settledValues: number[] = [1, 2, 3];

    let bowlRadius = 0;
    let diceSize = 0;
    // Bowl + dice share one footprint. The dome opens downward (rim at the bowl
    // container's y=0, dome curving UP to -r), so the dice live just inside the rim
    // and the closed dome genuinely covers them. bowlCoverY is the rim's y in root
    // coords when the lid is down; diceBaseY is the dice centre, tucked under the dome.
    let bowlCoverY = 0;
    let diceBaseY = 0;
    let diceGap = 0;

    // ---- draw helpers ----
    // Rectangular lid (ฝาครอบ) — same bounding box as the old hemisphere
    // (width 2r, height r, bottom edge at y=0) so cover/lift/shake geometry
    // and the dice tuck underneath are untouched.
    function drawBowl() {
      bowlDome.clear();
      bowlRim.clear();

      const r = bowlRadius;
      const w = r * 2;
      const h = r;
      const corner = Math.max(8, r * 0.14);
      const knobR = r * 0.1;

      // Knob (จุก) on top so the slab reads as a lid.
      bowlDome
        .ellipse(0, -h - knobR * 0.35, knobR, knobR * 0.7)
        .fill({ color: palette.lid, alpha: 1 })
        .ellipse(0, -h - knobR * 0.35, knobR, knobR * 0.7)
        .stroke({ width: Math.max(1.5, r * 0.025), color: palette.lidEdge, alpha: 1 });

      // Lid body
      bowlDome
        .roundRect(-w / 2, -h, w, h, corner)
        .fill({ color: palette.lid, alpha: 1 })
        .roundRect(-w / 2, -h, w, h, corner)
        .stroke({ width: Math.max(2, r * 0.04), color: palette.lidEdge, alpha: 1 });

      // Top sheen
      bowlDome
        .roundRect(-w / 2 + w * 0.07, -h + h * 0.1, w * 0.86, h * 0.32, corner * 0.8)
        .fill({ color: 0xffffff, alpha: palette.sheen });

      // Rim band at the bottom edge
      bowlRim
        .rect(-r, -4, r * 2, 8)
        .fill({ color: palette.rim, alpha: 1 });
      bowlRim
        .rect(-r, -4, r * 2, 8)
        .stroke({ width: 1, color: palette.rimEdge, alpha: 0.8 });
    }

    function drawOneDie(idx: number, value: number, highlight: boolean) {
      const faceG = diceFaceGfx[idx];
      const glowG = diceGlowGfx[idx];
      faceG.clear();
      glowG.clear();

      const s = diceSize;
      const hs = s / 2;
      const r = s * 0.12; // corner radius

      // Glow halo for winning dice
      if (highlight) {
        glowG
          .roundRect(-hs - 6, -hs - 6, s + 12, s + 12, r + 4)
          .fill({ color: primary, alpha: 0.38 });
      }

      // Die body
      faceG
        .roundRect(-hs, -hs, s, s, r)
        .fill({ color: palette.die, alpha: 1 })
        .roundRect(-hs, -hs, s, s, r)
        .stroke({ width: Math.max(1, s * 0.02), color: palette.dieEdge, alpha: 0.6 });

      // Pips
      const pipColor = highlight ? (palette.pipHit ?? primary) : palette.pip;
      drawDieFace(faceG, value, s, pipColor);
    }

    function relayout() {
      if (stage) {
        stage.fit(app.screen.width, app.screen.height);
        const k = app.screen.width / FRAME.width;
        const fontSize = Math.max(28, Math.round(FRAME.height * 0.16 * k));
        sumText.style.fontSize = fontSize;
        // On the bright red-and-gold stage the total needs its own edge to stay crisp.
        sumText.style.stroke = { color: 0x4a0610, width: Math.max(4, fontSize * 0.14), join: 'round' };
        sumText.style.dropShadow = { color: 0x2a0004, alpha: 0.55, blur: 4, distance: 2, angle: Math.PI / 2 };
        const above = stage.toCanvas(FRAME.width / 2, stage.dieCentre(1).y - FRAME.height * 0.2);
        sumText.position.set(above.x, above.y);
        return;
      }
      // The board lives below the stage in normal flow, so the bowl owns the whole
      // square — scale it up to fill the stage instead of floating in empty space.
      const min = Math.min(app.screen.width, app.screen.height);
      bowlRadius = Math.max(72, min * 0.4);
      diceSize = Math.max(40, min * 0.155);
      diceGap = diceSize * 1.25;

      // Centre the dome attractively about the root origin: with the rim at +r/2 the
      // dome spans [-r/2, +r/2] vertically, so the whole hemisphere sits balanced.
      bowlCoverY = bowlRadius * 0.5;
      // Dice centre sits just inside the rim, under the dome footprint. Their span
      // (3 × diceSize across, plus gaps) fits comfortably under the r=0.4·min dome.
      diceBaseY = bowlCoverY - diceSize * 0.62;

      root.position.set(app.screen.width / 2, app.screen.height / 2 - min * 0.02);

      drawBowl();
      bowlContainer.position.set(0, bowlCoverY);

      // Dice sit side by side directly under where the closed dome covers them.
      for (let i = 0; i < 3; i++) {
        diceCons[i].position.set((i - 1) * diceGap, diceBaseY);
        drawOneDie(i, settledValues[i] ?? 1, false);
        diceCons[i].alpha = animPhase === 'idle' || animPhase === 'closing' ? 0 : 1;
      }

      // Sum readout sits in the space the bowl vacates, above the revealed dice.
      sumText.style.fontSize = Math.max(40, Math.round(min * 0.17));
      sumText.position.set(root.position.x, root.position.y + bowlCoverY - bowlRadius * 0.9);
    }

    // The sum lives on the app's stage (not `root`) so both drawings place it in canvas pixels.
    app.stage.addChild(sumText);
    relayout();
    const onResize = () => relayout();
    app.renderer.on('resize', onResize);

    /** Swaps the vector drawing for the 3D render once every layer has decoded. */
    function applyArt(t3d: Record<DicePiece, Texture>) {
      stage = new DiceStage(t3d);
      root.visible = false;
      app.stage.addChildAt(stage.root, 0);
      stage.setLid(lidPose);
      const showing = animPhase !== 'idle' && animPhase !== 'closing' && animPhase !== 'shaking';
      stage.showDice(showing ? faceValues({ dice: settledValues, sum: 0, win: false }) : null);
      if (showing) stage.setLid((lidPose = lidLeavePose(1, FRAME.height)));
      relayout();
    }

    // ---- reveal helpers ----
    function fireReveal(roundId: string) {
      if (firedRoundId === roundId) return;
      firedRoundId = roundId;
      playSfx('reveal');
      stateRef.current.onRevealComplete();
    }

    function startAnimation(roundId: string, pres: DicePresentation) {
      startedRoundId = roundId;
      firedRoundId = null;
      elapsed = 0;
      lastSfxAt = 0;
      settledValues = pres.dice.length >= 3 ? pres.dice.slice(0, 3) : [1, 1, 1];

      // If the lid is currently open (previous round revealed, or mid-lift), play the
      // 'closing' phase first: the dome drops back down over the dice and the old dice +
      // sum fade out under it, THEN we shake. A replay goes revealed → playing WITHOUT an
      // 'idle' frame, so this branch is what gives play-2 its lid-set-down beat.
      const lidIsOpen = animPhase !== 'idle' && animPhase !== 'closing';
      if (lidIsOpen) {
        closeFromY = bowlContainer.position.y;
        closeFromAlpha = bowlContainer.alpha;
        closeFromScale = bowlContainer.scale.x;
        closeDiceFromAlpha = diceCons[0].alpha;
        closeSumFromAlpha = sumText.alpha;
        closeFromLid = lidPose;
        animPhase = 'closing';
        playSfx('diceRoll');
        return;
      }

      // Fresh start from idle (lid already covering, dice hidden) — go straight to shaking,
      // from the scale the idle breathing left it at.
      animPhase = 'shaking';
      shakeFromScale = bowlContainer.scale.x;
      bowlContainer.position.set(0, bowlCoverY);
      bowlContainer.alpha = 1;
      sumText.alpha = 0;
      for (const dc of diceCons) { dc.alpha = 0; dc.scale.set(1); dc.rotation = 0; }
      stage?.setGlint(0);
    }

    // Which dice are "winners" for a given bet?
    function winningDiceIndices(bet: DiceBet | null | undefined, dice: number[], pres: DicePresentation): Set<number> {
      if (!pres.win || !bet) return new Set();
      // For hi/lo, all dice contribute — highlight all
      if (bet === 'high' || bet === 'low') {
        return new Set([0, 1, 2]);
      }
      // triple-any: all same
      if (bet === 'triple-any') return new Set([0, 1, 2]);
      // triple-N: all same specific
      const tripleM = /^triple-(\d+)$/.exec(bet);
      if (tripleM) {
        const n = parseInt(tripleM[1]);
        if (dice.every((d) => d === n)) return new Set([0, 1, 2]);
      }
      // pair-N: two matching
      const pairM = /^pair-(\d+)$/.exec(bet);
      if (pairM) {
        const n = parseInt(pairM[1]);
        const matched = new Set<number>();
        let count = 0;
        for (let i = 0; i < 3; i++) { if (dice[i] === n) { matched.add(i); count++; } }
        if (count >= 2) return matched;
      }
      // single-N: at least one matching
      const singleM = /^single-(\d+)$/.exec(bet);
      if (singleM) {
        const n = parseInt(singleM[1]);
        const matched = new Set<number>();
        for (let i = 0; i < 3; i++) { if (dice[i] === n) matched.add(i); }
        return matched;
      }
      // total-N: all contribute
      const totalM = /^total-(\d+)$/.exec(bet);
      if (totalM) return new Set([0, 1, 2]);
      return new Set();
    }

    function setArtLid(p: LidPose) {
      lidPose = p;
      stage?.setLid(p);
    }

    function restArt() {
      if (!stage) return;
      stage.setRig(RIG_REST);
      setArtLid(LID_SHUT);
      stage.showDice(null);
      for (let i = 0; i < 3; i++) { stage.setGlow(i, 0); stage.settleDie(i, 0, 1, 1); }
    }

    // ---- ticker ----
    const tick = (tk: Ticker) => {
      const dt = tk.deltaMS;
      const { phase: ph, envelope: env, turbo: tb } = stateRef.current;
      const bet = stateRef.current.selectionBet;
      const reduced = prefersReducedMotion();
      stage?.update(dt / 16.67);

      const closeDur = reduced
        ? CLOSE_DURATION_REDUCED_MS
        : tb ? CLOSE_DURATION_MS * 0.4 : CLOSE_DURATION_MS;
      const shakeDur = reduced
        ? SHAKE_DURATION_REDUCED_MS
        : tb ? SHAKE_DURATION_MS * 0.35 : SHAKE_DURATION_MS;
      const liftDur = tb ? LIFT_DURATION_MS * 0.4 : LIFT_DURATION_MS;
      const tumbleDur = tb ? TUMBLE_DURATION_MS * 0.4 : TUMBLE_DURATION_MS;

      // Trigger animation when we enter playing with a fresh envelope
      // A fresh roundId always (re)starts the animation — even mid-'done' from a prior round,
      // because single-shot replay transitions revealed → playing without a 'idle' frame.
      if (ph === 'playing' && env && startedRoundId !== env.roundId) {
        const pres = readPresentation(env.outcome.presentation);
        if (pres) startAnimation(env.roundId, pres);
      }

      // Reset to idle when phase goes idle
      if (ph === 'idle' && animPhase !== 'idle') {
        animPhase = 'idle';
        startedRoundId = null;
        firedRoundId = null;
        for (const dc of diceCons) { dc.alpha = 0; dc.scale.set(1); dc.rotation = 0; }
        bowlContainer.position.set(0, bowlCoverY);
        bowlContainer.alpha = 1;
        bowlContainer.scale.set(1);
        sumText.alpha = 0;
        restArt();
      }

      // ---- idle: the vector lid breathes; the gold one now and then catches the light ----
      if (animPhase === 'idle') {
        if (stage) {
          const t = (performance.now() % GLINT_PERIOD_MS) / GLINT_PASS_MS;
          stage.setGlint(reduced ? 0 : t);
          return;
        }
        const breathe = IDLE_BREATHE_AMPLITUDE * Math.sin(performance.now() / 700);
        bowlContainer.scale.set(1 + breathe);
        return;
      }

      elapsed += dt;

      if (animPhase === 'closing') {
        const t = Math.min(1, elapsed / closeDur);
        const ease = easeOutCubic(t);
        // Dome drops from its lifted/faded spot back down over the dice, becoming solid.
        bowlContainer.position.set(0, closeFromY + (bowlCoverY - closeFromY) * ease);
        bowlContainer.alpha = closeFromAlpha + (1 - closeFromAlpha) * ease;
        bowlContainer.scale.set(closeFromScale + (1 - closeFromScale) * ease);
        // Old dice + sum fade out under the descending lid.
        const fade = 1 - ease;
        for (const dc of diceCons) dc.alpha = closeDiceFromAlpha * fade;
        sumText.alpha = closeSumFromAlpha * fade;
        if (stage) {
          // The gold lid comes back down over the dish; the old dice go once it covers them.
          setArtLid(lidClosePose(t, closeFromLid));
          for (let i = 0; i < 3; i++) stage.setGlow(i, 0);
        }

        if (t >= 1) {
          animPhase = 'shaking';
          elapsed = 0;
          lastSfxAt = 0;
          shakeFromScale = 1;
          bowlContainer.position.set(0, bowlCoverY);
          bowlContainer.alpha = 1;
          bowlContainer.scale.set(1);
          sumText.alpha = 0;
          for (const dc of diceCons) { dc.alpha = 0; dc.scale.set(1); dc.rotation = 0; }
          if (stage) {
            setArtLid(LID_SHUT);
            stage.showDice(null);
            playSfx('ballBounce', 0);
          }
        }
        return;
      }

      if (animPhase === 'shaking') {
        const t = Math.min(1, elapsed / shakeDur);
        if (stage) {
          // The dealer lifts the shut dish, shakes it hard, and sets it down with a thud.
          stage.setRig(rigShakePose(elapsed, shakeDur, FRAME.height, reduced));
        } else {
          const pose = shakePose(elapsed, shakeDur, bowlRadius, shakeFromScale);
          bowlContainer.position.set(pose.x, bowlCoverY + pose.y);
          bowlContainer.scale.set(pose.scale);
        }

        // Throttled rattle SFX
        if (elapsed - lastSfxAt >= DICE_SHAKE_SFX_INTERVAL_MS * (1 - t * t * 0.5)) {
          playSfx('diceShake');
          lastSfxAt = elapsed;
        }

        if (t >= 1) {
          animPhase = 'lifting';
          elapsed = 0;
          // Where the shake left the vector lid: its lift starts from there.
          shakeEnd = { x: bowlContainer.position.x, y: bowlContainer.position.y - bowlCoverY, scale: bowlContainer.scale.x };
          playSfx('diceRoll');
          if (stage) {
            stage.setRig(RIG_REST);
            // This round's faces are already lying in the dish: the opening lid shows them.
            stage.showDice(faceValues({ dice: settledValues, sum: 0, win: false }));
          }
        }
        return;
      }

      if (animPhase === 'lifting') {
        const t = Math.min(1, elapsed / liftDur);
        if (stage) {
          setArtLid(lidLiftPose(t, FRAME.height));
        } else {
          // Bowl lifts upward and fades out, revealing the dice in place beneath it, gliding
          // back to the centre from wherever the shake left it.
          const pose = liftPose(t, shakeEnd, bowlRadius);
          bowlContainer.position.set(pose.x, bowlCoverY + pose.y);
          bowlContainer.scale.set(pose.scale);
          bowlContainer.alpha = pose.alpha;
        }

        if (t >= 1) {
          animPhase = 'tumbling';
          elapsed = 0;
          landed = 0;
          // Reveal the dice that were hidden under the bowl, already drawn with THIS round's
          // real faces (settledValues) so they match the result from the first frame — the
          // settle step only re-draws to add the win highlight, never to change the values.
          for (let i = 0; i < 3; i++) {
            diceCons[i].alpha = 1;
            diceCons[i].scale.set(stage ? 1 : 0.1);
            drawOneDie(i, settledValues[i] ?? 1, false);
          }
        }
        return;
      }

      if (animPhase === 'tumbling') {
        const t = Math.min(1, elapsed / tumbleDur);

        if (stage) {
          // The lid is carried away while the dice clack into place one after another.
          setArtLid(lidLeavePose(t, FRAME.height));
          for (let i = 0; i < 3; i++) {
            const s = dieSettle(i, reduced ? 1 : t, FRAME.height);
            stage.settleDie(i, s.dy, s.scaleX, s.scaleY);
            if (!reduced && landed === i && t >= dieLandsAt(i)) {
              playSfx('ballBounce', i);
              landed++;
            }
          }
        } else {
          // Dice tumble in (scale up) and settle with individual offsets
          for (let i = 0; i < 3; i++) {
            const phaseOffset = i * 0.08;
            const localT = Math.max(0, Math.min(1, (t - phaseOffset) / (1 - phaseOffset)));
            const localEase = easeOutBack(localT);
            diceCons[i].scale.set(localEase);
            // Slight rotation during tumble
            diceCons[i].rotation = (1 - localEase) * (0.8 - i * 0.25);
          }
        }

        if (t >= 1) {
          animPhase = 'settling';
          elapsed = 0;
          // Snap rotations
          for (const dc of diceCons) { dc.scale.set(1); dc.rotation = 0; }

          const env = stateRef.current.envelope;
          if (env) {
            const pres = readPresentation(env.outcome.presentation);
            const vals = pres?.dice ?? settledValues;
            const winSet = pres ? winningDiceIndices(bet, vals, pres) : new Set<number>();
            for (let i = 0; i < 3; i++) {
              drawOneDie(i, vals[i] ?? 1, winSet.has(i));
              stage?.setGlow(i, winSet.has(i) ? 0.28 : 0);
              stage?.settleDie(i, 0, 1, 1);
            }

            // Show the total so players read the result instantly (green on a win).
            sumText.text = String(pres?.sum ?? vals.reduce((a, b) => a + (b ?? 0), 0));
            sumText.style.fill = pres?.win ? success : 0xffffff;
            sumText.alpha = 1;
            sumScale = stage && !reduced ? 0.4 : 1;
            sumText.scale.set(sumScale);

            // Check near-miss
            if (pres && isNearMissHiLo(pres.sum, bet) && !pres.win) {
              nearMissFlicker = NEAR_MISS_FLICKER_MS;
            }

            // Spinning gold coins out of the dish, the card's shower; a few, still, for reduced motion.
            if (stage && pres?.win) {
              stage.burst(reduced ? 4 : 12, reduced);
              playSfx('coin');
            }
          }
        }
        return;
      }

      if (animPhase === 'settling') {
        const SETTLE_FLASH = tb ? 100 : 200;
        // The total pops in over the settle.
        if (sumScale < 1) {
          const p = Math.min(1, elapsed / SETTLE_FLASH);
          sumText.scale.set(0.4 + 0.6 * easeOutBack(p));
        }
        if (elapsed >= SETTLE_FLASH) {
          animPhase = 'done';
          elapsed = 0;
          winPulseElapsed = 0;
          sumText.scale.set(1);
          sumScale = 1;

          const env = stateRef.current.envelope;
          if (env) fireReveal(env.roundId);
        }
        return;
      }

      if (animPhase === 'done') {
        // Win pulse on winning dice
        const env = stateRef.current.envelope;
        const pres = env ? readPresentation(env.outcome.presentation) : null;
        if (pres?.win) {
          winPulseElapsed += dt;
          const pt = Math.min(1, winPulseElapsed / WIN_PULSE_DURATION_MS);
          const pulse = Math.sin(pt * Math.PI * 3) * 0.08 * easeOutQuart(1 - pt);
          const vals = pres.dice;
          const winSet = winningDiceIndices(bet, vals, pres);
          for (let i = 0; i < 3; i++) {
            if (winSet.has(i)) {
              diceCons[i].scale.set(1 + pulse);
              stage?.settleDie(i, 0, 1 + pulse, 1 + pulse);
            }
          }
        }

        // Near-miss flicker
        if (nearMissFlicker > 0) {
          nearMissFlicker -= dt;
          const f = Math.sin(elapsed / 25) > 0 ? 0.5 : 1;
          for (const dc of diceCons) dc.alpha = f;
          if (stage) stage.root.alpha = f === 1 ? 1 : 0.75;
          if (nearMissFlicker <= 0) {
            nearMissFlicker = 0;
            for (const dc of diceCons) dc.alpha = 1;
            if (stage) stage.root.alpha = 1;
          }
        }
        return;
      }
    };

    app.ticker.add(tick);

    void preloadPieces('dice', DICE_PIECES, preferredDensity(window.devicePixelRatio)).then((imgs) => {
      if (!imgs || disposed) return;
      tex = toTextures(imgs);
      applyArt(tex);
    });

    return () => {
      disposed = true;
      if (tex) for (const t3d of Object.values(tex)) t3d.destroy(true);
      tex = null;
      stage = null;
      app.ticker.remove(tick);
      app.renderer.off('resize', onResize);
    };
  }).current;

  // Start fetching the art with the component, ahead of the Pixi init the scene waits on.
  useEffect(() => {
    void preloadPieces('dice', DICE_PIECES, preferredDensity(window.devicePixelRatio));
  }, []);

  const hostRef = usePixiScene(onReady);

  return (
    // The bowl is a hemisphere — a 16:9 canvas hugs it without the dead bands a
    // square stage leaves above and below, so the page stays compact.
    <div className="relative grid place-items-center w-full min-h-[180px] lg:min-h-[clamp(220px,34vh,320px)] overflow-hidden p-3">
      <div
        ref={hostRef}
        className="relative"
        style={{ width: 'min(100%, 520px)', aspectRatio: '16 / 9' }}
        aria-hidden
      />
      {phase === 'idle' && (
        <span className="absolute bottom-2 rounded-full bg-black/35 px-2.5 py-0.5 text-[12px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('dice.roll')}
        </span>
      )}
    </div>
  );
}
